import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Library, BookOpen, Users, TrendingUp, Upload,
  Plus, ArrowRight, Clock, CheckCircle2, AlertCircle,
} from 'lucide-react';
import StatCard from '../../components/common/StatCard';
import { useAppContext } from '../../context/AppContext';
import { useRoleData } from '../../hooks/useRoleData';
import './Dashboard.css';
import { formatDate } from '../../utils/dateFormatter';

const Dashboard = () => {
  const { currentUser, openUploadModal } = useAppContext();
  const isTrainee = currentUser?.role === 'Trainee';
  const navigate = useNavigate();
  const {
    filteredContent,
    filteredModules,
    filteredAnalytics,
    filteredUsers,
    personalAnalytics,
    canUpload,
    canCreateModule,
    canEnroll,
    canViewSystemAnalytics,
    canViewModuleAnalytics,
    canViewPersonalAnalytics,
  } = useRoleData();

  const { stats } = filteredAnalytics;

  const recentContent = [...filteredContent]
    .sort((a, b) => new Date(b.uploadDate) - new Date(a.uploadDate))
    .slice(0, 4);

  const activeModules = filteredModules.filter((m) => m.status === 'Active').slice(0, 4);
  const traineeModuleProgress = new Map(
    (personalAnalytics?.moduleProgress || []).map((module) => [module.moduleId, Number(module.progress) || 0])
  );

  const getModuleProgressValue = (module) => {
    if (isTrainee) {
      // Trainees only ever see their OWN progress — never fall back to the
      // module's aggregate completion rate (that reflects all trainees).
      return traineeModuleProgress.get(module.id) ?? 0;
    }

    return Number(module.completionRate) || 0;
  };

  const traineeCompletedModules = new Set(
    (personalAnalytics?.moduleProgress || []).filter((m) => m.completed).map((m) => m.moduleId)
  );

  const progressStat = isTrainee
    ? {
        icon: TrendingUp,
        label: 'Overall Progress',
        value: `${stats?.avgCompletionRate ?? 0}%`,
        sub: 'Across your enrolled modules',
        color: 'yellow',
        trend: 5,
      }
    : {
        icon: TrendingUp,
        label: 'Avg. Completion',
        value: `${stats?.avgCompletionRate ?? 0}%`,
        sub: 'Across all modules',
        color: 'yellow',
        trend: 5,
      };

  const getHourOfDay = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <div className="dashboard">
      {/* Welcome Banner */}
      <div className="dashboard__welcome">
        <div className="dashboard__welcome-content">
          <div className="dashboard__welcome-tag">
            <span className="badge badge-gray">{currentUser?.role || 'Administrator'}</span>
          </div>
          <h1 className="dashboard__welcome-title">
            {getHourOfDay()}, {currentUser.firstName || currentUser.name.split(' ')[0]}
          </h1>
          <p className="dashboard__welcome-sub">
            {currentUser?.role === 'Instructor' ? 'Here\'s what\'s happening with your modules today.' :
             currentUser?.role === 'Trainee' ? 'Here\'s your training progress and available content.' :
             'Here\'s what\'s happening on the NLNG VR Training Platform today.'}
          </p>
          <div className="dashboard__welcome-actions">
            {canUpload && (
              <button className="btn btn-primary" onClick={openUploadModal}>
                <Upload size={15} />
                Upload Content
              </button>
            )}
            {canCreateModule && (
              <button className="btn btn-outline" onClick={() => navigate('/modules')}>
                <Plus size={15} />
                New Module
              </button>
            )}
          </div>
        </div>
        <div className="dashboard__welcome-graphic">
          <div className="dashboard__vr-ring dashboard__vr-ring--1" />
          <div className="dashboard__vr-ring dashboard__vr-ring--2" />
          <div className="dashboard__vr-ring dashboard__vr-ring--3" />
          <div className="dashboard__vr-headset">
            <span>VR</span>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className={`dashboard__stats ${isTrainee ? 'dashboard__stats--trainee' : ''}`}>
        {[
          { icon: Library, label: 'Total VR Content', value: stats.totalContent, sub: 'Across all modules', color: 'green', trend: 12 },
          { icon: BookOpen, label: 'Active Modules', value: stats.activeModules, sub: `${filteredModules.length} total modules`, color: 'dark', trend: 8 },
          ...(isTrainee ? [] : [
            { icon: Users, label: 'Enrolled Users', value: stats.enrolledUsers, sub: '10 trainees · 5 instructors', color: 'blue', trend: 20 },
          ]),
          progressStat,
        ].map(({ icon: Icon, label, value, sub, color, trend }) => (
          <StatCard
            key={label}
            icon={Icon}
            label={label}
            value={value}
            sub={sub}
            color={color}
            trend={trend}
          />
        ))}
      </div>

      {/* Main Grid */}
      <div className="dashboard__grid">
        {/* Recent Content */}
        <div className="card dashboard__card">
          <div className="card-header">
            <h3 className="dashboard__card-title">Recent Content</h3>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => navigate('/content-library')}
            >
              View all <ArrowRight size={13} />
            </button>
          </div>
          <div className="card-body dashboard__card-body">
            <div className="dashboard__content-list">
              {recentContent.map((c) => {
                const formattedUploadDate = formatDate(c.uploadDate);
                return (
                  <div
                    key={c.id}
                    className="dashboard__content-item"
                    onClick={() => navigate(`/content-library/${c.contentId ? c.contentId.replace(/-/g, '') : c.id}`, { state: { fromDashboard: true } })}
                  >
                    <div className="dashboard__content-thumb">
                      <Library size={18} />
                    </div>
                    <div className="dashboard__content-info">
                      <span className="dashboard__content-name">{c.title}</span>
                      <div className="dashboard__content-meta">
                        <span className="badge badge-green">{c.category}</span>
                        <span className="dashboard__content-date">
                          <Clock size={11} /> {formattedUploadDate}
                        </span>
                      </div>
                    </div>
                    <span className={`badge ${c.status === 'Active' ? 'badge-green' : 'badge-gray'}`}>
                      {c.status}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Active Modules */}
        <div className="card dashboard__card">
          <div className="card-header">
            <h3 className="dashboard__card-title">Active Modules</h3>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => navigate('/modules')}
            >
              View all <ArrowRight size={13} />
            </button>
          </div>
          <div className="card-body dashboard__card-body">
            <div className="dashboard__module-list">
              {activeModules.map((m) => {
                const progressValue = getModuleProgressValue(m);

                return (
                <div
                  key={m.id}
                  className="dashboard__module-item"
                  onClick={() => navigate(`/modules/${m.moduleId ? m.moduleId.replace(/-/g, '') : m.id}`, { state: { fromDashboard: true } })}
                >
                  <div className="dashboard__module-header">
                    <span className="dashboard__module-name">{m.title}</span>
                    <span className="dashboard__module-pct">{progressValue}%</span>
                  </div>
                  <div className="progress-bar">
                    <div
                      className="progress-bar-fill"
                      style={{ width: `${progressValue}%` }}
                    />
                  </div>
                  <div className="dashboard__module-footer">
                    {isTrainee ? (
                      <span>
                        {progressValue >= 100 || traineeCompletedModules.has(m.id) ? (
                          <><CheckCircle2 size={11} /> Completed</>
                        ) : progressValue > 0 ? (
                          <><Clock size={11} /> In progress</>
                        ) : (
                          <><AlertCircle size={11} /> Not started</>
                        )}
                      </span>
                    ) : (
                      <span>
                        <Users size={11} /> {m.traineeIds.length} enrolled
                      </span>
                    )}
                    <span>
                      <BookOpen size={11} /> {m.contentIds.length} content items
                    </span>
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Grid */}
      <div className={`dashboard__bottom-grid ${isTrainee ? 'dashboard__bottom-grid--trainee' : ''}`}>
        {/* Quick Actions */}
        <div className="card dashboard__quick-actions">
          <div className="card-header">
            <h3 className="dashboard__card-title">Quick Actions</h3>
          </div>
          <div className="card-body">
            <div className="dashboard__actions-grid">
              {[
                canUpload && { label: 'Upload VR Content', icon: Upload, action: openUploadModal, color: 'green' },
                canCreateModule && { label: 'Add New Module', icon: Plus, action: () => navigate('/modules'), color: 'blue' },
                canEnroll && { label: 'Enroll Trainees', icon: Users, action: () => navigate('/enrollment'), color: 'yellow' },
                (canViewSystemAnalytics || canViewModuleAnalytics || canViewPersonalAnalytics) && { label: 'View Analytics', icon: TrendingUp, action: () => navigate('/analytics'), color: 'dark' },
              ].filter(Boolean).map(({ label, icon: Icon, action, color }) => (
                <button
                  key={label}
                  className={`dashboard__action-btn dashboard__action-btn--${color}`}
                  onClick={action}
                >
                  <div className="dashboard__action-icon">
                    <Icon size={20} />
                  </div>
                  <span>{label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Recent Users - Hidden for Trainee */}
        {!isTrainee && (
          <div className="card dashboard__recent-users">
            <div className="card-header">
              <h3 className="dashboard__card-title">Recent Users</h3>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => navigate('/enrollment')}
              >
                View all <ArrowRight size={13} />
              </button>
            </div>
            <div className="card-body dashboard__card-body">
              <div className="dashboard__user-list">
                {filteredUsers.slice(0, 5).map((u) => (
                  <div key={u.id} className="dashboard__user-item">
                    <div className="avatar" style={{ width: 36, height: 36, fontSize: '0.75rem' }}>
                      {u.initials}
                    </div>
                    <div className="dashboard__user-info">
                      <span className="dashboard__user-name">{u.name}</span>
                      <span className="dashboard__user-dept">{u.department}</span>
                    </div>
                    <div className="dashboard__user-status">
                      {u.role === 'Instructor' ? (
                        <span className="badge badge-blue">{u.role}</span>
                      ) : (
                        <span className="badge badge-green">{u.role}</span>
                      )}
                    </div>
                    {u.status === 'Active' ? (
                      <CheckCircle2 size={16} className="dashboard__user-active" />
                    ) : (
                      <AlertCircle size={16} className="dashboard__user-inactive" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
